import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AppProvider } from "./context/AppContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { initOneSignal } from "./services/onesignal";
import { trackPageView } from "./services/analytics";
import PushNotificationPrompt from "./components/common/PushNotificationPrompt";
import { CookieConsentBanner } from "./components/common/CookieConsentBanner";
import InstallAppPrompt from "./components/common/InstallAppPrompt";
import BackExitGuard from "./components/common/BackExitGuard";
import UnsubscribePage from "./pages/UnsubscribePage";
import WhyMintedPage from "./pages/WhyMintedPage";
import { isNative, initializeNativeFeatures, initNativeOneSignal } from "./capacitor";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import SellPage from "./pages/SellPage";
import MyListingsPage from "./pages/MyListingsPage";
import PrivateCollectionPage from "./pages/PrivateCollectionPage";
import MessagesPage from "./pages/MessagesPage";
import NotificationsPage from "./pages/NotificationsPage";
import ProfilePage from "./pages/ProfilePage";
import OrdersPage from "./pages/OrdersPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import CheckoutPage from "./pages/CheckoutPage";
import SellerProfilePage from "./pages/SellerProfilePage";
import CategoriesPage from "./pages/CategoriesPage";
import CategoryPage from "./pages/CategoryPage";
import WalletPage from "./pages/WalletPage";
import InviteFriendsPage from "./pages/InviteFriendsPage";
import FoundersWallPage from "./pages/FoundersWallPage";
import InviteCodePage from "./pages/InviteCodePage";
import PromotePage from "./pages/PromotePage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import AdminDashboard from "./pages/AdminDashboard";
import OffersPage from "./pages/OffersPage";
import EditListingPage from "./pages/EditListingPage";
import BrowseProductsPage from "./pages/BrowseProductsPage";
import JoinPage from "./pages/JoinPage";
import CompleteSignupPage from "./pages/CompleteSignupPage";
import StorePage from "./pages/StorePage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import ManageDataPage from "./pages/ManageDataPage";
import ItemsToShipPage from "./pages/ItemsToShipPage";
import AdminShippingDashboard from "./pages/AdminShippingDashboard";
import AuctionRoomPage from "./pages/AuctionRoomPage";
import AuctionDetailPage from "./pages/AuctionDetailPage";
import SellerAuctionRequestsPage from "./pages/SellerAuctionRequestsPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import StoresPage from "./pages/StoresPage";
import SavedSearchesPage from "./pages/SavedSearchesPage";
import WishlistPage from "./pages/WishlistPage";
import ServicesPage from "./pages/ServicesPage";
import ServiceCreatePage from "./pages/ServiceCreatePage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import SellerAnalyticsPage from "./pages/SellerAnalyticsPage";
import ChatbotWidget from "./components/ChatbotWidget";
import QueryButton from "./components/common/QueryButton";

// Component to track page views on route change
function PageTracker() {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);
  
  return null;
}

function App() {
  // Initialize native features and OneSignal on app mount
  useEffect(() => {
    if (isNative) {
      // Running as native iOS/Android app
      initializeNativeFeatures();
      initNativeOneSignal();
    } else {
      // Running as web app - use OneSignal for web push
      initOneSignal();
    }
  }, []);

  return (
    <HelmetProvider>
    <AppProvider>
    <CurrencyProvider>
      <div className="App">
        <BrowserRouter>
          <PageTracker />
          <BackExitGuard />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/why-minted" element={<WhyMintedPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/product/:id" element={<ProductDetailPage />} />
            <Route path="/sell" element={<SellPage />} />
            <Route path="/my-listings" element={<MyListingsPage />} />
            <Route path="/private-collection" element={<PrivateCollectionPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/messages/:conversationId" element={<MessagesPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/order/:orderId" element={<OrderTrackingPage />} />
            <Route path="/checkout/:productId" element={<CheckoutPage />} />
            <Route path="/seller/:sellerId" element={<SellerProfilePage />} />
            <Route path="/store/:storeId" element={<StorePage />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/category/:categoryId" element={<CategoryPage />} />
            <Route path="/products" element={<BrowseProductsPage />} />
            <Route path="/browse" element={<BrowseProductsPage />} />
            <Route path="/search" element={<BrowseProductsPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/invite" element={<InviteFriendsPage />} />
            <Route path="/founders-wall" element={<FoundersWallPage />} />
            <Route path="/invite/:referralCode" element={<InviteCodePage />} />
            <Route path="/promote/:productId" element={<PromotePage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/offers" element={<OffersPage />} />
            <Route path="/edit-listing/:productId" element={<EditListingPage />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/complete-signup" element={<CompleteSignupPage />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route path="/manage-data" element={<ManageDataPage />} />
            <Route path="/items-to-ship" element={<ItemsToShipPage />} />
            <Route path="/admin/shipping" element={<AdminShippingDashboard />} />
            <Route path="/auctions" element={<AuctionRoomPage />} />
            <Route path="/auction/:auctionId" element={<AuctionDetailPage />} />
            <Route path="/seller/auction-requests" element={<SellerAuctionRequestsPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/stores" element={<StoresPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/create" element={<ServiceCreatePage />} />
            <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
            <Route path="/seller/analytics" element={<SellerAnalyticsPage />} />
            <Route path="/recently-viewed" element={<BrowseProductsPage />} />
            <Route path="/saved-searches" element={<SavedSearchesPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
          </Routes>
          {/* Push Notification Prompt */}
          <PushNotificationPrompt />
          <CookieConsentBanner />
          {/* Install App Prompt (Add to Home Screen) */}
          <InstallAppPrompt />
          {/* Chatbot Widget */}
          <ChatbotWidget />
          {/* Ask a question (goes to admin Queries inbox via AI Support agent) */}
          <QueryButton />
        </BrowserRouter>
      </div>
    </CurrencyProvider>
    </AppProvider>
    </HelmetProvider>
  );
}

export default App;
